import React, { FC, useCallback, useEffect, useState } from 'react';
import { debounce } from 'lodash';
import Svg from 'svg-inline-react';
import { DropdownInput } from '@src/components/Select/DropdownInput';
import { useGetGoogleLocationsLazyQuery } from '@src/graphql/generated';
import FrSearchSvg from '@src/svgs/fr-search.svg';
import { Option } from '../Select/interfaces/option.interface';
import { PositionLocationDropdownItem } from './interfaces/positionLocationDropdownItem.interface';
import './styles.scss';

const MIN_SEARCH_PATTERN_LENGTH = 3;

export interface PositionLocationDropdownProps {
  defaultValue?: Option;
  isClearable?: boolean;
  isDisabled?: boolean;
  isInvalid?: boolean;
  onClear?: () => void;
  onSelect?: (newValue: PositionLocationDropdownItem) => void;
  placeholder?: string;
  shouldClearOnSelect?: boolean;
}

export const PositionLocationDropdown: FC<PositionLocationDropdownProps> = ({
  shouldClearOnSelect,
  defaultValue,
  onSelect,
  isDisabled,
  isInvalid,
  placeholder,
  onClear,
  isClearable,
}) => {
  const [resetKey, setResetKey] = useState<number>(0);

  const [searchPattern, setSearchPattern] = useState<string>('');

  const [selectedOption, setSelectedOption] = useState<Option | undefined>(defaultValue);

  const [fetchLocations, { data: locationsData, loading: isLocationsLoading }] = useGetGoogleLocationsLazyQuery();

  const options: Option[] =
    locationsData?.googleLocations.result.map(location => {
      return {
        label: location.longName,
        value: location.id,
      };
    }) || [];

  const handleSearch = useCallback(
    debounce((value: string) => {
      fetchLocations({
        variables: {
          searchPattern: value,
        },
      });
    }, 300),
    [],
  );

  const handleInputChange = (value: string): void => {
    setSearchPattern(value);

    const trimmedValue = value.trim();

    if (trimmedValue.length < MIN_SEARCH_PATTERN_LENGTH) {
      return;
    }

    handleSearch(trimmedValue);
  };

  useEffect(() => {
    if (defaultValue) {
      setSelectedOption(defaultValue);
    }
  }, [defaultValue?.value]);

  return (
    <DropdownInput
      key={resetKey}
      isInvalid={isInvalid}
      isLoading={isLocationsLoading}
      inputValue={searchPattern}
      onInputChange={handleInputChange}
      options={options}
      placeholder={placeholder}
      value={selectedOption}
      isClearable={isClearable}
      isDisabled={isDisabled}
      noOptionsMessage={() => 'Type to search'}
      components={{
        DropdownIndicator: () => {
          return (
            <div className="position-location-dropdown__search-wrapper">
              <Svg className="position-location-dropdown__search" src={FrSearchSvg} />
            </div>
          );
        },
      }}
      onChange={selectedOption => {
        if (selectedOption === null) {
          onClear?.();

          setSearchPattern('');

          setSelectedOption(undefined);

          return;
        }

        const selectedLocation = locationsData?.googleLocations.result.find(
          location => location.id === selectedOption.value,
        );

        if (selectedLocation) {
          onSelect?.(selectedLocation);
        }

        if (shouldClearOnSelect) {
          setSearchPattern('');

          setSelectedOption(undefined);

          setResetKey(prevKey => prevKey + 1);

          return;
        }

        setSelectedOption(selectedOption);
      }}
    />
  );
};
